import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api';
import { X, User, Award, CheckCircle, Clock, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import { getSsDueTextClassFromDays } from '../../utils/ssDueColor';
import { daysUntilDue } from '../../utils/dueDate';
import { applyInspectionDue } from '../../utils/inspectionDueDate';
import SelectionConfirmBar from '../../components/SelectionConfirmBar';

const WAITING_DAYS_PENDING = 3;

function WorkerSelectionModal({ job, onClose, onAssign, onSuccess }) {
  const queryClient = useQueryClient();
  const done = onSuccess || onAssign;
  const [selectionMode, setSelectionMode] = useState('direct');
  const [selectedWorkerId, setSelectedWorkerId] = useState(null);
  const [selectedForInquiry, setSelectedForInquiry] = useState(new Set());
  const [selectAllForQuery, setSelectAllForQuery] = useState(false);
  /** temporary = this job only · permanent = also building SS default IQP */
  const [replaceScope, setReplaceScope] = useState('temporary');

  const { data: availableWorkers, isLoading } = useQuery({
    queryKey: ['workers', 'iqp', job?.ss_code],
    queryFn: async () => {
      if (!job?.ss_code) return [];
      const response = await api.get(`/workers/iqp/${job.ss_code}`);
      return response.data.data || [];
    },
    enabled: !!job?.ss_code,
  });

  const { data: jobData } = useQuery({
    queryKey: ['job', job?.id],
    queryFn: async () => {
      const response = await api.get(`/jobs/${job.id}`);
      return response.data;
    },
    enabled: !!job?.id,
    refetchInterval: 5000,
  });

  const sendQueryMutation = useMutation({
    mutationFn: async (workerIds) => {
      const response = await api.post(`/jobs/${job.id}/query`, { workerIds });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Query sent to workers successfully');
      queryClient.invalidateQueries(['job', job.id]);
      setSelectedForInquiry(new Set());
      setSelectAllForQuery(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to send query');
    },
  });

  const assignWorkerMutation = useMutation({
    mutationFn: async (workerId) => {
      const response = await api.put(`/jobs/${job.id}/assign`, {
        workerId,
        permanent: replaceScope === 'permanent',
      });
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(
        data?.default_worker_updated
          ? 'Worker changed — also set as building default IQP for this SS'
          : 'Worker changed for this inspection'
      );
      queryClient.invalidateQueries(['job', job.id]);
      queryClient.invalidateQueries(['jobs']);
      queryClient.invalidateQueries(['ss-instances']);
      queryClient.invalidateQueries(['building']);
      if (typeof done === 'function') done();
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to change worker');
    },
  });

  const acceptedWorkers = jobData?.queries?.filter((q) => q.response === 'ACCEPT') || [];
  const acceptedCount = acceptedWorkers.length;
  const queriedWorkerIds = new Set((jobData?.queries || []).map((q) => q.worker_id));

  const selectedWorker =
    availableWorkers?.find((w) => w.workerId === selectedWorkerId) ||
    acceptedWorkers.find((q) => q.worker_id === selectedWorkerId);

  const handleConfirmAssign = () => {
    if (!selectedWorkerId) {
      toast.error('Select a worker first');
      return;
    }
    assignWorkerMutation.mutate(selectedWorkerId);
  };

  const toggleInquirySelection = (workerId) => {
    setSelectedForInquiry((prev) => {
      const next = new Set(prev);
      if (next.has(workerId)) next.delete(workerId);
      else next.add(workerId);
      return next;
    });
  };

  const handleSelectAllQueryable = () => {
    const ids = (availableWorkers || [])
      .filter((w) => {
        const isAssigned = jobData?.assigned_worker_id === w.workerId;
        return !isAssigned && !queriedWorkerIds.has(w.workerId);
      })
      .map((w) => w.workerId);
    setSelectedForInquiry(new Set(ids));
    setSelectAllForQuery(true);
  };

  const handleConfirmSendInquiry = () => {
    if (selectedForInquiry.size === 0) {
      toast.error('Select at least one worker');
      return;
    }
    sendQueryMutation.mutate([...selectedForInquiry]);
  };

  const busy = assignWorkerMutation.isPending || sendQueryMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Change worker</h2>
            <p className="text-sm text-gray-500 mt-1">
              SS Code:{' '}
              <span
                className={`font-bold ${getSsDueTextClassFromDays(
                  daysUntilDue(job ? applyInspectionDue(job).period_due : null)
                )}`}
              >
                {job?.ss_code}
              </span>
              {job?.ss_name && ` - ${job.ss_name}`}
            </p>
            <p className="text-xs text-gray-500 mt-1">Select a worker, choose temporary or permanent, then Confirm.</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {selectionMode === 'direct' && (
            <div className="mb-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold text-gray-900 mb-2">Replacement type</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <label className="inline-flex items-start gap-2 cursor-pointer text-sm text-gray-800">
                  <input
                    type="radio"
                    name="replaceScope"
                    value="temporary"
                    checked={replaceScope === 'temporary'}
                    onChange={() => setReplaceScope('temporary')}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="font-medium">Temporary</span>
                    <span className="block text-xs text-gray-500">
                      This inspection only — building default IQP stays unchanged.
                    </span>
                  </span>
                </label>
                <label className="inline-flex items-start gap-2 cursor-pointer text-sm text-gray-800">
                  <input
                    type="radio"
                    name="replaceScope"
                    value="permanent"
                    checked={replaceScope === 'permanent'}
                    onChange={() => setReplaceScope('permanent')}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="font-medium">Permanent</span>
                    <span className="block text-xs text-gray-500">
                      Also set this worker as the building SS default IQP for future notifies.
                    </span>
                  </span>
                </label>
              </div>
            </div>
          )}

          <div className="flex items-center space-x-2 mb-6 bg-gray-100 rounded-lg p-1">
            <button
              type="button"
              onClick={() => {
                setSelectionMode('direct');
                setSelectedWorkerId(null);
              }}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectionMode === 'direct'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Direct Assignment
            </button>
            <button
              type="button"
              onClick={() => {
                setSelectionMode('reverse-auction');
                setSelectedWorkerId(null);
              }}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectionMode === 'reverse-auction'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Query / Auction (legacy)
            </button>
          </div>

          {selectionMode === 'reverse-auction' && (
            <div className="mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-gray-900 mb-2">Send inquiry</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Check workers below (or Select all), then Confirm send inquiry.
                </p>
                <button
                  type="button"
                  onClick={handleSelectAllQueryable}
                  className="text-sm font-medium text-primary-700 hover:text-primary-900"
                >
                  Select all available ({availableWorkers?.length || 0})
                </button>
              </div>

              {acceptedCount > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">
                    Accepted Workers — select one, then Confirm change
                  </h3>
                  <div className="space-y-2">
                    {acceptedWorkers
                      .sort((a, b) => new Date(a.responded_at) - new Date(b.responded_at))
                      .map((query, index) => {
                        const worker = availableWorkers?.find((w) => w.workerId === query.worker_id);
                        const selected = selectedWorkerId === query.worker_id;
                        return (
                          <button
                            type="button"
                            key={query.id}
                            onClick={() => setSelectedWorkerId(query.worker_id)}
                            className={`w-full text-left border rounded-lg p-4 ${
                              selected
                                ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                                : 'border-green-200 bg-green-50 hover:border-green-400'
                            }`}
                          >
                            <div className="flex items-center space-x-3">
                              <CheckCircle className="w-5 h-5 text-green-600" />
                              <div>
                                <p className="font-medium text-gray-900">
                                  {worker?.workerName || query.worker_id}
                                  {index === 0 && (
                                    <span className="ml-2 px-2 py-0.5 bg-green-600 text-white rounded text-xs">
                                      First
                                    </span>
                                  )}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Accepted: {new Date(query.responded_at).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          )}

          <div>
            <h3 className="font-semibold text-gray-900 mb-3">
              IQP List ({availableWorkers?.length || 0})
              {selectionMode === 'direct' && ' — select one, then Confirm change'}
            </h3>
            {isLoading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : availableWorkers && availableWorkers.length > 0 ? (
              <div className="space-y-2">
                {availableWorkers.map((worker) => {
                  const hasAccepted = acceptedWorkers.some((q) => q.worker_id === worker.workerId);
                  const isAssigned = jobData?.assigned_worker_id === worker.workerId;
                  const isRejected = jobData?.queries?.some(
                    (q) => q.worker_id === worker.workerId && q.response === 'REJECT'
                  );
                  const query = jobData?.queries?.find((q) => q.worker_id === worker.workerId);
                  const sentAt = query?.sent_at ? new Date(query.sent_at) : null;
                  const respondedAt = query?.responded_at ? new Date(query.responded_at) : null;
                  const waitingDays =
                    sentAt && !respondedAt
                      ? Math.floor((Date.now() - sentAt.getTime()) / (1000 * 60 * 60 * 24))
                      : null;
                  const isPending = sentAt && !respondedAt && waitingDays >= WAITING_DAYS_PENDING;
                  const alreadyQueried = queriedWorkerIds.has(worker.workerId);
                  const canSelectInquiry = !isAssigned && !alreadyQueried;
                  const selected = selectedWorkerId === worker.workerId;

                  return (
                    <div
                      key={worker.workerId}
                      role={selectionMode === 'direct' ? 'button' : undefined}
                      tabIndex={selectionMode === 'direct' && !isAssigned && !isRejected ? 0 : undefined}
                      onClick={() => {
                        if (selectionMode === 'direct' && !isAssigned && !isRejected) {
                          setSelectedWorkerId(worker.workerId);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (
                          selectionMode === 'direct' &&
                          !isAssigned &&
                          !isRejected &&
                          (e.key === 'Enter' || e.key === ' ')
                        ) {
                          e.preventDefault();
                          setSelectedWorkerId(worker.workerId);
                        }
                      }}
                      className={`border rounded-lg p-4 ${
                        selected
                          ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                          : isAssigned
                            ? 'border-green-500 bg-green-50'
                            : hasAccepted
                              ? 'border-green-200 bg-green-50'
                              : isPending
                                ? 'border-yellow-400 bg-yellow-50'
                                : isRejected
                                  ? 'border-gray-200 bg-gray-50 opacity-50'
                                  : 'border-gray-200 bg-white hover:border-gray-300 cursor-pointer'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          {selectionMode === 'reverse-auction' && canSelectInquiry && (
                            <input
                              type="checkbox"
                              checked={selectedForInquiry.has(worker.workerId)}
                              onChange={() => toggleInquirySelection(worker.workerId)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 text-primary-600 rounded"
                            />
                          )}
                          {selectionMode === 'direct' && !isAssigned && !isRejected && (
                            <input
                              type="radio"
                              name="directWorker"
                              checked={selected}
                              onChange={() => setSelectedWorkerId(worker.workerId)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 text-primary-600"
                            />
                          )}
                          <User className="w-5 h-5 text-gray-400 shrink-0" />
                          <div className="min-w-0">
                            <p
                              className={`font-medium ${
                                isAssigned
                                  ? 'text-green-700'
                                  : isRejected
                                    ? 'text-gray-400'
                                    : isPending
                                      ? 'text-yellow-800'
                                      : 'text-gray-900'
                              }`}
                            >
                              {worker.workerName}
                              {isAssigned && <span className="ml-2 text-green-700">(Assigned)</span>}
                            </p>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 mt-1">
                              <span>License: {worker.licenseNumber || 'N/A'}</span>
                              <span className="flex items-center">
                                <Award className="w-4 h-4 mr-1" />
                                Grade: {worker.grade}
                              </span>
                              {sentAt && (
                                <span className="flex items-center">
                                  <Clock className="w-4 h-4 mr-1" />
                                  {respondedAt
                                    ? `Responded`
                                    : `Sent ${waitingDays != null ? waitingDays : 0}d ago`}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <User className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p>No available workers for this SS code</p>
              </div>
            )}
          </div>
        </div>

        {selectionMode === 'direct' && (
          <SelectionConfirmBar
            count={selectedWorkerId ? 1 : 0}
            label={
              selectedWorker?.workerName ||
              selectedWorker?.worker_id ||
              'worker selected'
            }
            confirmLabel="Confirm change"
            onConfirm={handleConfirmAssign}
            onClear={() => setSelectedWorkerId(null)}
            disabled={busy}
          />
        )}
        {selectionMode === 'reverse-auction' && selectedWorkerId && acceptedCount > 0 && (
          <SelectionConfirmBar
            count={1}
            label="accepted worker selected"
            confirmLabel="Confirm change"
            onConfirm={handleConfirmAssign}
            onClear={() => setSelectedWorkerId(null)}
            disabled={busy}
          />
        )}
        {selectionMode === 'reverse-auction' && selectedForInquiry.size > 0 && !selectedWorkerId && (
          <SelectionConfirmBar
            count={selectedForInquiry.size}
            label={`worker${selectedForInquiry.size === 1 ? '' : 's'} for inquiry`}
            confirmLabel={`Confirm send inquiry (${selectedForInquiry.size})`}
            onConfirm={handleConfirmSendInquiry}
            onClear={() => {
              setSelectedForInquiry(new Set());
              setSelectAllForQuery(false);
            }}
            disabled={busy}
          />
        )}
      </div>
    </div>
  );
}

export default WorkerSelectionModal;
